import { create } from 'zustand';

/** Editor preferences, persisted per browser. */
export interface Settings {
  /** inspector shows every raw Unity field (components, folders, sorting layers) */
  advanced: boolean;
  /** Objects panel lists internal / unused prefabs too */
  allPrefabs: boolean;
  /** object placement and drag snapping in tiles (0 = free) */
  snap: number;
  /** "Make moving platform" takes the objects standing on or inside the shape along */
  convertTakesObjects: boolean;
  showGrid: boolean;
  showLinks: boolean;
  /** left tool bar stays expanded with labels instead of only expanding on hover */
  toolbarPinned: boolean;
  /** mark terrain cells on slopes their style has no tile for */
  showMissing: boolean;
  /** width of the right side panel in px (drag its left edge) */
  sideWidth: number;
  /** draw the level camera's bounds (and edit them with the select tool) */
  showCameraBounds: boolean;
  /** tile editor: tool bar stays expanded */
  teToolbarPinned: boolean;
  /** tile editor: widths of the column beside the canvas and the right panel (px) */
  teRefsWidth: number;
  teSideWidth: number;
  /** lobby bar shrunk to a small pill */
  lobbyBarMin: boolean;
  /** lobby: colour of your cursor and dot ('#rrggbb'; '' picks one from your account) */
  lobbyColor: string;
  /** lobby: name shown above your cursor ('' uses your email before the @) */
  lobbyName: string;
}

const KEY = 'ple:settings';
const DEFAULTS: Settings = { advanced: false, allPrefabs: false, snap: 0.5, convertTakesObjects: true, showGrid: true, showLinks: true, toolbarPinned: false, showMissing: true, sideWidth: 340, showCameraBounds: true, teToolbarPinned: false, teRefsWidth: 170, teSideWidth: 330, lobbyBarMin: false, lobbyColor: '', lobbyName: '' };

function load(): Settings {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    // ignore unreadable settings
  }
  return { ...DEFAULTS };
}

interface SettingsState extends Settings {
  update(p: Partial<Settings>): void;
  reset(): void;
}

export const useSettings = create<SettingsState>((set, get) => ({
  ...load(),
  update(p) {
    set(p);
    save(get());
  },
  reset() {
    set({ ...DEFAULTS });
    save(get());
  },
}));

function save(s: SettingsState) {
  const out: Partial<Settings> = {};
  for (const k of Object.keys(DEFAULTS) as (keyof Settings)[]) (out as any)[k] = s[k];
  try {
    globalThis.localStorage?.setItem(KEY, JSON.stringify(out));
  } catch {
    // storage full or blocked
  }
}

export function settings(): Settings {
  return useSettings.getState();
}

export function snapStep(): number {
  return useSettings.getState().snap;
}
